import React, { useEffect, useRef } from 'react';
import { ConfirmationDataProps } from '@msc-b2c/interfaces/types';
import styles from './paymentconfirmation.module.scss';
import {
  formatPrice,
  formatPriceWithoutRounding,
  getActiveSession,
  getCurrencySymbol,
  getPriceCurrencyOrder,
  replacePlaceholders,
  RootState,
  useGetBookingQuery,
} from '@msc-b2c/shared-utils';
import { skipToken } from '@reduxjs/toolkit/query';
import { CabinI } from '@msc-b2c/interfaces/interfaces';
import {
  cabinOfTheLoggedUser,
  getCabinIdOfTheLoggedUser,
  getItineraryNameByCanvas,
} from '../../utils/myAreaUtils';
import {
  getPaymentTypeFromSession,
  triggerBookingPaymentFailureEvent,
  triggerBookingPurchaseEvent,
} from '../../utils/myAreaAnalytics';
import { Button, Image, Text } from '@msc-b2c/shared-components';
import { useSelector } from 'react-redux';
import { BookingStatusEnum, PaymentTypeEnum } from '@msc-b2c/interfaces/enums';

export function PaymentConfirmation({
  title,
  emailText,
  alternativeText,
  confirmationLinkLabel,
  confirmationLinkUrl,
  imgReference,
  imgAlt,
  confirmationDetails,
  paymentDetails,
}: Readonly<ConfirmationDataProps>) {
  const currency = getCurrencySymbol();
  const { title: confirmationTitle, bookingNumberText, itineraryText } = confirmationDetails;
  const {
    title: paymentTitle,
    totalAmountText,
    orderTotalAmountText,
    depositAmountText,
  } = paymentDetails;

  const { email, bookingNumber = '', passengerId } = getActiveSession();

  const { data: booking } = useGetBookingQuery(bookingNumber || skipToken);

  const userEmail =
    email || booking?.passengers?.find((passenger) => passenger.id === passengerId)?.email;
  const cabinIDOfTheLoggedUser = getCabinIdOfTheLoggedUser(booking);
  const cabinLoggedUser: CabinI | undefined = cabinOfTheLoggedUser(
    booking?.cabins,
    cabinIDOfTheLoggedUser
  );
  const area = getItineraryNameByCanvas(cabinLoggedUser?.cruise?.demographicArea ?? '');

  const { type, price } = useSelector((state: RootState) => state.payment.selectedPayment) || {};
  const finalizeBookingPaymentResponse = useSelector(
    (state: RootState) => state.myCruise.finalizeBookingPayment
  );
  const { membershipData, cabinDetails } = useSelector((state: RootState) => state.myCruise);

  const hasTriggeredPurchaseRef = useRef(false);

  useEffect(() => {
    if (!booking || !finalizeBookingPaymentResponse || hasTriggeredPurchaseRef.current) {
      return;
    }

    const transactionId = finalizeBookingPaymentResponse.transactionId;
    const hasWarnings = (finalizeBookingPaymentResponse._warnings ?? []).length > 0;

    if (transactionId && !hasWarnings) {
      triggerBookingPurchaseEvent({
        booking,
        loggedUserId: passengerId || '',
        transactionId,
        transactionType: booking?.status.code === BookingStatusEnum.OPTIONAL ? type : 'balance',
        paymentType: getPaymentTypeFromSession(),
        paymentAmount: price || '0',
        membershipData,
        cabinDetails,
        totalDiscount: 0,
        checkoutPaymentEmail: userEmail,
      });
      hasTriggeredPurchaseRef.current = true;
    } else if (hasWarnings) {
      triggerBookingPaymentFailureEvent({
        booking,
        loggedUserId: passengerId || '',
        transactionId: transactionId || '',
        transactionType: booking?.status.code === BookingStatusEnum.OPTIONAL ? type : 'balance',
        paymentType: getPaymentTypeFromSession(),
        paymentAmount: price || '0',
        membershipData,
        cabinDetails,
        totalDiscount: 0,
        checkoutPaymentEmail: userEmail,
      });
      hasTriggeredPurchaseRef.current = true;
    }
  }, [
    booking,
    finalizeBookingPaymentResponse,
    type,
    price,
    membershipData,
    cabinDetails,
    passengerId,
    userEmail,
  ]);

  return (
    <div className={styles['confirmation']}>
      <div className={styles['confirmation__icon']}>
        <Image
          src={imgReference}
          alt={imgAlt ?? ''}
        />
      </div>
      <Text
        text={title}
        tagHtml={'h1'}
        styleOption={'header-l'}
      />

      <Text
        styleOption={'confirmation__message'}
        text={
          !userEmail
            ? alternativeText
            : replacePlaceholders(emailText, { bookingPaymentEmail: userEmail })
        }
      />

      {confirmationLinkLabel && confirmationLinkUrl && (
        <Button
          text={confirmationLinkLabel ?? ''}
          accessibilityLabel={confirmationLinkLabel ?? ''}
          buttonLink={confirmationLinkUrl}
          styleOption={'cmp-button__link confirmation__button-link'}
        />
      )}

      <div className={styles['confirmation__section']}>
        <Text
          tagHtml={'h2'}
          styleOption={'body-big confirmation__section-title'}
          text={confirmationTitle}
        />
        <Text
          styleOption={'confirmation__label'}
          text={replacePlaceholders(bookingNumberText, {
            bookingNumber: `${bookingNumber}`,
          })}
        />
        <Text
          styleOption={'confirmation__label'}
          text={replacePlaceholders(itineraryText, {
            itineraryName: `${area}`,
          })}
        />
      </div>

      <div className={styles['confirmation__section']}>
        <Text
          tagHtml={'h2'}
          styleOption={'body-big confirmation__section-title'}
          text={paymentTitle}
        />
        <div className={styles['confirmation__payment-line']}>
          {type === PaymentTypeEnum.DEPOSIT ? (
            <>
              <Text
                styleOption={'body-big confirmation__label confirmation__summary-label'}
                text={depositAmountText}
              />
              <Text
                styleOption={'confirmation__value'}
                text={replacePlaceholders(getPriceCurrencyOrder(), {
                  currency: currency,
                  price: formatPriceWithoutRounding(price),
                })}
              />
            </>
          ) : (
            <>
              <Text
                styleOption={'body-big confirmation__label confirmation__summary-label'}
                text={totalAmountText}
              />
              <Text
                styleOption={'confirmation__value'}
                text={replacePlaceholders(getPriceCurrencyOrder(), {
                  currency: currency,
                  price: formatPriceWithoutRounding(price),
                })}
              />
            </>
          )}
        </div>
        <div
          className={`${styles['confirmation__payment-line']} ${styles['confirmation__payment-line--total']}`}
        >
          <Text
            styleOption={'body-big confirmation__label confirmation__summary-label'}
            text={orderTotalAmountText}
          />
          <Text
            styleOption={'confirmation__value'}
            text={replacePlaceholders(getPriceCurrencyOrder(), {
              currency: currency,
              price: formatPrice(price),
            })}
          />
        </div>
      </div>
    </div>
  );
}
